import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { Layers, Pencil, Plus, Printer, Tags } from 'lucide-react'
import { useMemo, useState, type ReactNode } from 'react'

import type { PriceListEntrySummary, PriceListSummary } from '@aeris/contracts'
import {
  Badge,
  Button,
  EmptyState,
  InlineAlert,
  Input,
  ListToolbar,
  Page,
  PageHeader,
  Pagination,
  QueryState,
  ResultCount,
  SearchInput,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  SortableHeaderCell,
  TableHeaderCell,
  TableRow,
  VisuallyHidden,
} from '@aeris/ui'

import { PriceListForm } from '../components/price-list-form.tsx'
import { PriceTiersDialog } from '../components/price-tiers-dialog.tsx'
import {
  ApiError,
  fetchPriceEntries,
  fetchPriceLists,
  setPriceItem,
  fetchVariantLabelInfo,
} from '../lib/api.ts'
import { useAuth } from '../lib/auth-context.ts'
import { addToLabelQueue, droppedMessage } from '../lib/label-queue.ts'
import {
  formatUnitPrice,
  parseTypedPrice,
  toEditableAmount,
  toWireAmount,
} from '../lib/price-format.ts'
import { useDebounced } from '../lib/use-debounced.ts'
import { sortKey, useTableSort } from '../lib/use-table-sort.ts'

/**
 * Prices.
 *
 * The screen is product-centric rather than price-centric: it lists the
 * catalogue and shows what each product costs in the chosen table, including
 * the products that cost nothing yet. A screen that listed only existing
 * prices could never show the work that remains, which is the work.
 *
 * Amounts are formatted by `formatMoney` and read back by `parseHumanAmount`,
 * both from `@aeris/money`. Nothing here does arithmetic on a `number`, and
 * nothing here formats with `Intl` — which takes one. ADR-0016.
 *
 * Writing is gated on `pricing.write`. That only hides the controls; the API
 * refuses the request independently, which is what actually enforces it. An
 * operator holds `pricing.read`: they see the price, they do not set it.
 */

const PAGE_SIZE = 25

type PriceCellProps = {
  entry: PriceListEntrySummary
  canWrite: boolean
  onSave: (input: { value: string; entry: PriceListEntrySummary }) => void
  saving: boolean
}

/**
 * The price of one product, edited where it is read.
 *
 * A read view and an edit view rather than a permanently open input: a table of
 * fifty text boxes is a form nobody can scan, and a price that looks editable
 * to somebody holding only `pricing.read` is a lie the server then has to
 * correct.
 */
const PriceCell = ({
  entry,
  canWrite,
  onSave,
  saving,
}: PriceCellProps): ReactNode => {
  const [editing, setEditing] = useState(false)
  const [text, setText] = useState(() => toEditableAmount(entry.basePrice))

  const label = `Preço de ${entry.sku}`

  if (!canWrite || !editing) {
    const shown =
      entry.basePrice === null ? 'Sem preço' : formatUnitPrice(entry.basePrice)

    if (!canWrite) {
      return (
        <span
          className={
            entry.basePrice === null
              ? 'text-(--text-muted)'
              : 'font-(--font-mono)'
          }
        >
          {shown}
        </span>
      )
    }

    return (
      <button
        type="button"
        aria-label={label}
        onClick={() => {
          setText(toEditableAmount(entry.basePrice))
          setEditing(true)
        }}
        className={[
          'rounded-(--radius-sm) px-1 py-0.5 text-left',
          'hover:bg-(--surface-sunken) focus-visible:outline-2 focus-visible:outline-(--border-focus)',
          entry.basePrice === null
            ? 'text-(--text-muted)'
            : 'font-(--font-mono)',
        ].join(' ')}
      >
        {shown}
      </button>
    )
  }

  const commit = (): void => {
    setEditing(false)
    const typed = text.trim()
    if (typed === toEditableAmount(entry.basePrice)) return
    onSave({ value: typed, entry })
  }

  return (
    <Input
      autoFocus
      aria-label={label}
      value={text}
      disabled={saving}
      inputMode="decimal"
      className="w-32 font-(--font-mono)"
      onChange={(event) => {
        setText(event.target.value)
      }}
      onBlur={commit}
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          event.preventDefault()
          commit()
        }
        if (event.key === 'Escape') {
          // Abandoning an edit must restore what was there, not save it.
          setText(toEditableAmount(entry.basePrice))
          setEditing(false)
        }
      }}
    />
  )
}

export const PrecosPage = (): ReactNode => {
  const { allows, session } = useAuth()
  const navigate = useNavigate()
  /*
   * "Preço salvo. Imprimir etiqueta?" (F124, origem "ao salvar preço").
   *
   * Um botão no próprio salvamento, e não uma caixa de entrada "o que mudou
   * desde ontem": o preço só muda por duas telas, e oferecer a etiqueta no
   * instante em que ele muda é a hora em que a pessoa sabe que precisa dela.
   */
  const [justSaved, setJustSaved] = useState<PriceListEntrySummary | null>(null)
  const [labelPending, setLabelPending] = useState(false)
  const queryClient = useQueryClient()

  const [selectedListId, setSelectedListId] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)

  // Back to the first page: a new order at the old offset shows rows that
  // have nothing to do with the column that was clicked.
  const sort = useTableSort<'name' | 'basePrice'>('name', 'asc', () => {
    setPage(1)
  })
  const [error, setError] = useState<string | null>(null)
  const [listFormOpen, setListFormOpen] = useState(false)
  const [editingList, setEditingList] = useState<PriceListSummary | undefined>(
    undefined
  )
  const [tiersFor, setTiersFor] = useState<PriceListEntrySummary | null>(null)

  const debouncedSearch = useDebounced(search)
  const canWrite = allows('pricing.write')

  const lists = useQuery({
    queryKey: ['price-lists'],
    queryFn: ({ signal }) => fetchPriceLists(signal),
  })

  // The default list is where the screen opens, because it is the one every
  // unqualified price lookup resolves against.
  const activeList: PriceListSummary | undefined = useMemo(() => {
    const available = lists.data?.priceLists ?? []
    if (selectedListId !== null) {
      const chosen = available.find((list) => list.id === selectedListId)
      if (chosen !== undefined) return chosen
    }
    return available.find((list) => list.isDefault) ?? available[0]
  }, [lists.data, selectedListId])

  const entries = useQuery({
    queryKey: [
      'price-entries',
      { list: activeList?.id, search: debouncedSearch, page, ...sortKey(sort) },
    ],
    enabled: activeList !== undefined,
    queryFn: ({ signal }) =>
      fetchPriceEntries(
        activeList?.id ?? '',
        {
          ...(debouncedSearch.trim().length > 0
            ? { search: debouncedSearch.trim() }
            : {}),
          page,
          pageSize: PAGE_SIZE,
          sort: sort.sort,
          direction: sort.direction,
        },
        signal
      ),
    placeholderData: (previous) => previous,
  })

  const savePrice = useMutation({
    mutationFn: async ({
      value,
      entry,
    }: {
      value: string
      entry: PriceListEntrySummary
    }) => {
      if (activeList === undefined) {
        throw new ApiError(409, 'no_price_list', 'Nenhuma tabela selecionada.')
      }

      const amount = parseTypedPrice(value)
      if (amount === null) {
        throw new ApiError(
          422,
          'invalid_price',
          'Informe um valor, por exemplo 19,90.'
        )
      }

      return await setPriceItem(activeList.id, {
        variantId: entry.variantId,
        unitPrice: toWireAmount(amount),
        minQuantity: '0',
        // Sent only when a price is being changed. Its absence means "create",
        // and a tier that already exists is then reported rather than silently
        // overwritten.
        ...(entry.baseVersion === null ? {} : { version: entry.baseVersion }),
      })
    },
    onSuccess: async (_saved, variables) => {
      setError(null)
      /*
       * Só na tabela PADRÃO. A etiqueta da prateleira imprime o preço base de
       * lá; mudar a tabela de atacado não muda o que está colado na peça, e
       * oferecer a etiqueta ali convidaria a reimprimir a prateleira inteira
       * sem motivo.
       */
      setJustSaved(activeList?.isDefault === true ? variables.entry : null)
      await queryClient.invalidateQueries({ queryKey: ['price-entries'] })
    },
    onError: (cause: unknown) => {
      setError(
        cause instanceof ApiError
          ? cause.message
          : 'Não foi possível salvar o preço.'
      )
    },
  })

  const total = entries.data?.total ?? 0
  const items = entries.data?.items ?? []
  const searching = debouncedSearch.trim().length > 0

  const queueLabel = (saved: PriceListEntrySummary): void => {
    const tenantId = session?.context?.tenantId ?? null
    if (tenantId === null) return
    setLabelPending(true)
    // Lido de novo no catálogo: o preço que vai no adesivo é o que ficou
    // gravado, com GTIN e SKU que esta linha não traz.
    fetchVariantLabelInfo([saved.variantId])
      .then(async (info) => {
        const { dropped } = addToLabelQueue(
          tenantId,
          info.items.map((item) => ({
            variantId: item.variantId,
            productId: item.productId,
            name: item.name,
            sku: item.sku,
            gtin: item.gtin,
            salePrice: item.salePrice,
            brand: item.brand,
            unit: item.unit,
            quantity: 1,
          }))
        )
        setJustSaved(null)
        const full = droppedMessage(dropped)
        if (full !== null) {
          setError(full)
          return
        }
        await navigate({ to: '/etiquetas' })
      })
      .catch(() => {
        setError('Não foi possível buscar o produto para a etiqueta.')
      })
      .finally(() => {
        setLabelPending(false)
      })
  }

  return (
    <Page>
      <PageHeader
        title="Preços"
        description="Tabelas de preço e faixas por quantidade. O preço não fica no cadastro do produto."
        actions={
          canWrite ? (
            <Button
              variant="primary"
              size="sm"
              onClick={() => {
                setEditingList(undefined)
                setListFormOpen(true)
              }}
              leadingIcon={<Plus aria-hidden="true" className="h-4 w-4" />}
            >
              Nova tabela
            </Button>
          ) : undefined
        }
      />

      {justSaved === null ? null : (
        <InlineAlert
          tone="success"
          title={`Preço de ${justSaved.productName} salvo.`}
          action={
            <>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setJustSaved(null)
                }}
              >
                Agora não
              </Button>
              <Button
                variant="primary"
                size="sm"
                loading={labelPending}
                leadingIcon={<Printer aria-hidden="true" className="h-4 w-4" />}
                onClick={() => {
                  queueLabel(justSaved)
                }}
              >
                Imprimir etiqueta
              </Button>
            </>
          }
        >
          Imprimir etiqueta com o preço novo?
        </InlineAlert>
      )}

      {error === null ? null : <InlineAlert tone="danger">{error}</InlineAlert>}

      {/* A busca primeiro, como em toda lista (F132); a tabela de preço é o
          filtro ao lado. */}
      <ListToolbar
        actions={
          canWrite && activeList !== undefined ? (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                setEditingList(activeList)
                setListFormOpen(true)
              }}
              leadingIcon={
                <Pencil aria-hidden="true" className="h-3.5 w-3.5" />
              }
            >
              Editar tabela
            </Button>
          ) : undefined
        }
      >
        <SearchInput
          value={search}
          onChange={(event) => {
            setSearch(event.target.value)
            setPage(1)
          }}
          placeholder="Buscar por nome, código ou código de barras"
          aria-label="Buscar produtos"
        />

        <Select
          value={activeList?.id ?? ''}
          onChange={(event) => {
            setSelectedListId(event.target.value)
            setPage(1)
          }}
          aria-label="Tabela de preço"
          className="w-64"
        >
          {(lists.data?.priceLists ?? []).map((list) => (
            <option key={list.id} value={list.id}>
              {list.name}
              {list.isDefault ? ' (padrão)' : ''}
            </option>
          ))}
        </Select>
      </ListToolbar>

      <ResultCount
        page={page}
        pageSize={PAGE_SIZE}
        total={total}
        loading={lists.isPending || entries.isPending}
        none="Nenhum produto encontrado"
      />

      {/*
        Duas consultas, dois estados (F132). Antes só as TABELAS entravam na
        cadeia: com os itens fora do ar, a tela dizia "Nenhum produto
        cadastrado" para uma loja com cinco mil.
      */}
      <QueryState
        isPending={lists.isPending}
        isError={lists.isError}
        error={lists.error}
        onRetry={() => {
          void lists.refetch()
        }}
        what="as tabelas de preço"
        isEmpty={(lists.data?.priceLists.length ?? 0) === 0}
        empty={
          <EmptyState
            icon={<Tags aria-hidden="true" className="h-8 w-8" />}
            title="Nenhuma tabela de preço"
            description={
              canWrite
                ? 'Crie a primeira tabela. Ela será a padrão da conta.'
                : 'Peça a um gerente para criar uma tabela de preço.'
            }
          />
        }
      >
        <QueryState
          isPending={entries.isPending}
          isError={entries.isError}
          error={entries.error}
          onRetry={() => {
            void entries.refetch()
          }}
          what="os preços"
          isEmpty={items.length === 0}
          filtered={searching}
          onClearFilters={() => {
            setSearch('')
            setPage(1)
          }}
          empty={
            <EmptyState
              icon={<Tags aria-hidden="true" className="h-8 w-8" />}
              title="Nenhum produto cadastrado"
              description="Cadastre produtos para definir preços."
            />
          }
        >
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeaderCell>Código</TableHeaderCell>
                  <SortableHeaderCell {...sort.column('name')}>
                    Produto
                  </SortableHeaderCell>
                  <TableHeaderCell>Un.</TableHeaderCell>
                  <SortableHeaderCell numeric {...sort.column('basePrice')}>
                    Preço
                  </SortableHeaderCell>
                  <TableHeaderCell>Faixas</TableHeaderCell>
                  <TableHeaderCell>
                    <VisuallyHidden>Ações</VisuallyHidden>
                  </TableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((entry) => (
                  <TableRow key={entry.variantId}>
                    <TableCell className="font-(--font-mono) whitespace-nowrap">
                      {entry.sku}
                    </TableCell>
                    <TableCell>{entry.productName}</TableCell>
                    <TableCell className="text-(--text-secondary)">
                      {entry.unit}
                    </TableCell>
                    <TableCell>
                      <PriceCell
                        // Keyed by the value it was built from, so a saved price
                        // re-renders from the server's answer rather than from
                        // stale local state.
                        key={`${entry.variantId}:${entry.basePrice ?? 'none'}`}
                        entry={entry}
                        canWrite={canWrite}
                        saving={savePrice.isPending}
                        onSave={(input) => {
                          savePrice.mutate(input)
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      {entry.tierCount > 1 ? (
                        <Badge tone="info">{entry.tierCount} faixas</Badge>
                      ) : (
                        <span className="text-(--text-muted)">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setTiersFor(entry)
                        }}
                        leadingIcon={
                          <Layers aria-hidden="true" className="h-3.5 w-3.5" />
                        }
                      >
                        Faixas
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </QueryState>
      </QueryState>

      <Pagination
        page={page}
        pageSize={PAGE_SIZE}
        total={total}
        onPageChange={setPage}
      />

      {listFormOpen ? (
        <PriceListForm
          key={editingList?.id ?? 'new'}
          priceList={editingList}
          onClose={() => {
            setListFormOpen(false)
          }}
        />
      ) : null}

      {tiersFor === null || activeList === undefined ? null : (
        <PriceTiersDialog
          key={tiersFor.variantId}
          priceListId={activeList.id}
          entry={tiersFor}
          canWrite={canWrite}
          onClose={() => {
            setTiersFor(null)
          }}
        />
      )}
    </Page>
  )
}
